<template>
  <div class="image-upload">
    <ImagePreviewModal ref="imagePreviewModal"
                       :image="image"></ImagePreviewModal>
    <div v-if="!image">
      <div class="row">
        <div class="col">
          <div class="image-upload-dropzone p-2 text-center">
            <div class="image-upload-dropzone-content p-2">
              <div class="row">
                <Icon :title="name"
                      class="col"
                      name="image2"></Icon>
              </div>
              <div class="mt-2 row">
                <span class="col">
                  {{ tr('administration.theme.TX_MAX_IMG_SIZE') }}
                  ({{ accept }})
                </span>
              </div>
            </div>
            <input :id="name"
                   :accept="accept"
                   class="input-file"
                   type="file"
                   @change="onFileChange">
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <div :style="{ backgroundColor: backgroundColor }"
           class="preview"
           @mouseenter="showActions"
           @mouseleave="hideActions">
        <BImg :src="image"
              fluid
              thumbnail
              class="p-0">
        </BImg>
        <div v-if="showOverlay"
             class="overlay-buttons w-100 d-flex justify-content-center">
          <ImageActionsOverlay @remove-image="removeImage"
                               @show-image="openImagePreviewModal">
          </ImageActionsOverlay>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { v4 as uuid } from 'uuid';
  import { BImg } from 'bootstrap-vue';
  import FileReaderFactory from '../file/FileReaderFactory';
  import WindowProvider from '~/zf/frontend/common/window/WindowProvider';
  import ImagePreviewModal from './ImagePreviewModal.vue';
  import ImageType from './ImageTypes';
  import ImageActionsOverlay from './ImageActionsOverlay.vue';
  import ImageEndpoint from './ImageEndpoint';
  import HttpService from '~/zf/frontend/common/http/HttpService';
  import EventService from '~/zf/frontend/common/event/EventService';
  import VirusEvent from '~/zf/frontend/vcom/events/VirusEvent';
  import Icon from '../layout/Icon.vue';
  import AlertService from '~/zf/frontend/common/alert/AlertService';
  import { translator } from '~/zf/frontend/common/mixins';
  import BootstrapVariant from '~/zf/frontend/vendor/bootstrap/BootstrapVariant';

  /*
    [Vue migration] Developer Note
    This component has a twin version for both Vue 2 and Vue 3 projects.
    When making changes, remember to update and test both versions.
  */
  export default {
    compatConfig: {
      MODE: 3,
      COMPONENT_FUNCTIONAL: true,
      COMPONENT_V_MODEL: true,
    },
    components: {
      Icon,
      ImageActionsOverlay,
      ImagePreviewModal,
      BImg,
    },
    mixins: [
      translator,
    ],
    props: {
      acceptedTypes: {
        type: Array,
        default: () => [
          ImageType.JPG.mime,
          ImageType.JPEG.mime,
          ImageType.PNG.mime,
        ],
      },
      backgroundColor: {
        type: String,
      },
      image: {
        type: String,
      },
      name: {
        type: String,
        default: () => uuid(),
      },
    },
    emits: ['is-loading', 'image-changed'],
    data() {
      return {
        BootstrapVariant,
        touched: false,
        showOverlay: false,
      };
    },
    computed: {
      accept() {
        const imageTypes = Object.values(ImageType);
        const activeImageTypes = imageTypes.filter((type) => this.acceptedTypes.includes(type.mime));
        const imageExtensions = activeImageTypes.map((type) => type.extension);

        return imageExtensions.join(', ');
      },
    },
    methods: {
      onFileChange(event) {
        const files = event.target.files || event.dataTransfer.files;

        if (files.length === 0) {
          return;
        }

        const file = files[0];
        const { size } = file;
        const maxSizeInBytes = 1048575;

        if (this.isTypeAccepted(file) === false) {
          AlertService.showError(this.tr('common.ACCEPTED_FILE_TYPES', [this.accept]));
          this.clearInput();
          return;
        }

        if (size >= maxSizeInBytes) {
          AlertService.showError(this.tr('administration.theme.TX_MAX_IMG_SIZE_ERROR'));
          this.clearInput();
          return;
        }

        this.touched = true;
        this.createImage(file);
      },
      isTypeAccepted(file) {
        return this.acceptedTypes.includes(file.type);
      },
      createImage(file) {
        this.$emit('is-loading', true);
        const reader = FileReaderFactory.make();

        reader.onload = (event) => {
          WindowProvider.setTimeout(async () => {
            const image = event.target.result;
            const hasImageVirus = await this.hasImageVirus(image);
            if (hasImageVirus === false) {
              this.$emit('image-changed', { data: event.target.result, type: file.type, name: file.name });
            }
            this.$emit('is-loading', false);
          }, 1000);
        };
        reader.readAsDataURL(file);
      },
      async hasImageVirus(image) {
        try {
          await HttpService.post(ImageEndpoint.CHECK, { image });
        } catch (error) {
          if (error.response?.data?.isFileMalicious !== undefined) {
            EventService.dispatch(VirusEvent.OPEN_VIRUS_FOUND_MODAL);
            return true;
          }
        }
        return false;
      },
      removeImage() {
        this.$emit('image-changed', null);
        this.touched = true;
      },
      openImagePreviewModal() {
        this.$refs.imagePreviewModal.show();
      },
      showActions() {
        this.showOverlay = true;
      },
      hideActions() {
        this.showOverlay = false;
      },
      clearInput() {
        const inputField = WindowProvider.document.getElementById(this.name);
        inputField.value = null;
      },
    },
  };
</script>

<style lang="scss" scoped>
    .image-upload-dropzone span.icomoon {
      font-size: 55px;
    }

    .image-upload-dropzone.col {
      height: 106px;
    }

    .image-upload-dropzone-content {
      border-style: dashed;
      border-width: 2px;
      height: 94px;
    }

    .input-file {
      opacity: 0;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      cursor: pointer;
    }

    /* https://stackoverflow.com/questions/7554845/the-cursorpointer-property-doesnt-apply-to-file-upload-buttons-in-webkit-brows */
    ::-webkit-file-upload-button {
      cursor: pointer;
    }

    .relative {
      position: relative;
    }

    .img-fluid {
      max-height: 106px;
    }

    .preview {
      display: inline-block;
      height: 100%;
      transition: 0.5s ease;
      position: relative;
    }
</style>
